/**
 * Shared Google sign-in primitives (admin + account apps).
 *
 *   <GoogleSignInButton href="/api/auth/google?scope=…" label="…" />
 *       "Continue with Google" button - full-page navigation into the redirect flow
 *       (robust, works even when One Tap is blocked in the browser).
 *
 *   useGoogleOneTap({ clientId, onCredential })
 *       Loads the Google Identity Services script and shows the One Tap prompt
 *       (dribbble-style "sign in right away"). `onCredential` receives the id_token,
 *       which the app posts to `/api/auth/google/onetap`.
 */
import * as React from "react";
import { cn } from "../lib/utils";

// ── GSI script single-loader ─────────────────────────────────────────────────
const GSI_SRC = "https://accounts.google.com/gsi/client";
let gsiPromise: Promise<void> | null = null;

function loadGsi(): Promise<void> {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if ((window as any).google?.accounts?.id) return Promise.resolve();
  if (gsiPromise) return gsiPromise;
  gsiPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("gsi_load_failed")));
      if ((window as any).google?.accounts?.id) resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = GSI_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("gsi_load_failed"));
    document.head.appendChild(s);
  });
  return gsiPromise;
}

// ── One Tap hook ─────────────────────────────────────────────────────────────
export interface GoogleOneTapOptions {
  /** Public OAuth client_id. Empty → the hook does nothing (Google not configured). */
  clientId: string;
  /** Called with a successful One Tap credential (id_token). */
  onCredential: (credential: string) => void;
  /** Do not show the prompt (e.g. when the user is already signing in / in the MFA flow). */
  disabled?: boolean;
}

/**
 * Shows the Google One Tap prompt when `clientId` is given and `disabled` is not set.
 * A safe no-op on the server / without a clientId / when the script does not load.
 */
export function useGoogleOneTap({ clientId, onCredential, disabled }: GoogleOneTapOptions): void {
  const cbRef = React.useRef(onCredential);
  cbRef.current = onCredential;

  React.useEffect(() => {
    if (!clientId || disabled || typeof window === "undefined") return;
    let cancelled = false;

    loadGsi()
      .then(() => {
        if (cancelled) return;
        const id = (window as any).google?.accounts?.id;
        if (!id) return;
        id.initialize({
          client_id: clientId,
          callback: (resp: { credential?: string }) => {
            if (resp?.credential) cbRef.current(resp.credential);
          },
          auto_select: false,
          cancel_on_tap_outside: true,
          use_fedcm_for_prompt: true,
        });
        id.prompt();
      })
      .catch(() => {
        /* One Tap is best-effort: an error must not break the page */
      });

    return () => {
      cancelled = true;
      try {
        (window as any).google?.accounts?.id?.cancel();
      } catch {
        /* ignore */
      }
    };
  }, [clientId, disabled]);
}

// ── "Continue with Google" button ────────────────────────────────────────────
function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58Z"
      />
    </svg>
  );
}

export interface GoogleSignInButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Redirect flow URL, e.g. `/api/auth/google?scope=admin`. */
  href: string;
  /** Button text (i18n from the app), e.g. "Continue with Google". */
  label: string;
}

/** Outline button that follows the Google brand guidelines and leads into the redirect flow. */
export const GoogleSignInButton = React.forwardRef<HTMLButtonElement, GoogleSignInButtonProps>(
  ({ href, label, className, disabled, onClick, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      onClick={(e) => {
        onClick?.(e);
        if (!e.defaultPrevented) window.location.href = href;
      }}
      className={cn(
        "inline-flex h-10 w-full items-center justify-center gap-3 rounded-md border border-border bg-card px-4 text-sm font-medium text-foreground transition-all",
        "hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50",
        "outline-none focus-visible:ring-2 focus-visible:ring-ring/40 active:scale-[0.98]",
        className,
      )}
      {...props}
    >
      <GoogleGlyph />
      {label}
    </button>
  ),
);
GoogleSignInButton.displayName = "GoogleSignInButton";
